import { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';

export interface ShipSubmission {
  courierPartnerId: string | null;
  courierKey: string | null;
  courierName: string;
  trackingNo: string;
}

interface CourierOption {
  id: string;
  courier_key: string;
  display_name: string;
}

// Manual "Ship" flow — lets staff pick from whichever couriers are enabled
// in Control Core (core_courier_partners) instead of free-typing a courier
// name. Enabling/disabling couriers and their credentials stays ERP-only;
// this just reads the same live config.
export function ShipModal({
  open,
  onClose,
  onSubmit,
  orderNo,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (submission: ShipSubmission) => void;
  orderNo: string;
  busy?: boolean;
}) {
  const [couriers, setCouriers] = useState<CourierOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [courierId, setCourierId] = useState('');
  const [trackingNo, setTrackingNo] = useState('');

  useEffect(() => {
    if (!open) return;
    setTrackingNo('');
    setLoading(true);
    supabase
      .from('core_courier_partners')
      .select('id, courier_key, display_name')
      .eq('is_enabled', true)
      .order('display_order', { ascending: true })
      .then(({ data }) => {
        const rows = (data as CourierOption[]) ?? [];
        setCouriers(rows);
        setCourierId(rows[0]?.id ?? '');
        setLoading(false);
      });
  }, [open]);

  const handleSubmit = () => {
    const selected = couriers.find((c) => c.id === courierId);
    if (!selected || !trackingNo.trim()) return;
    onSubmit({
      courierPartnerId: selected.id,
      courierKey: selected.courier_key,
      courierName: selected.display_name,
      trackingNo: trackingNo.trim(),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Shipment (배송 등록)" subtitle={orderNo} icon={<Truck className="w-4 h-4" />} busy={busy}>
      <div className="space-y-3">
        {loading ? null : couriers.length === 0 ? (
          <EmptyState title="No couriers enabled" hint="Enable a courier partner in Control Core ERP → Delivery Merchants first." />
        ) : (
          <>
            <Field label="Courier">
              <Select value={courierId} onChange={(e) => setCourierId(e.target.value)}>
                {couriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tracking No.">
              <Input required value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
            </Field>
            <Button className="w-full" disabled={!courierId || !trackingNo.trim()} loading={busy} onClick={handleSubmit}>
              Create Shipment
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
